import { Controller } from "@hotwired/stimulus";

// Fast Track screening gate.
//
// Two questions + a landlord attestation. The page mirrors the server: the clean
// exit is driven by Q2 (issues). "No issues" shows the clean-exit body and finalizes
// here; "Yes, there were issues" shows the redirect card that hands off to the full
// verification form.
//   • Clean-exit body (optional monthly rent + comments, attestation, "Submit
//     Reference") stays disabled until the attestation is checked. If the reference
//     would NOT rent again, a comment becomes required (matching the server) — the
//     one field that captures why on a clean exit.
//   • Redirect card routes to the full form, which re-collects those same fields.
//
// Both radios live in one form and always submit their actual values, so the
// server re-derives the same decision authoritatively (a direct POST is correct
// without JS). We never overwrite an answer client-side.
export default class extends Controller {
  static targets = [
    "rentAgainYes",
    "rentAgainNo",
    "issuesYes",
    "issuesNo",
    "cert",
    "attest",
    "submit",
    "redirect",
    "redirectSubmit",
    "detail",
    "comments",
    "commentsRequiredHint",
    "commentsOptional",
  ];

  connect() {
    this.refresh();
  }

  // Nothing resolves until both questions are answered — no redirect card, no
  // enabled submit — so the user is never shown a call-to-action (or a disabled
  // button) mid-decision. Once both are answered it's driven by Q2: "issues
  // reported" shows the redirect card to the full form; "no issues" shows the
  // clean-exit body (detail fields + attestation + submit).
  refresh() {
    if (!this.bothAnswered()) {
      // In progress → keep both calls-to-action hidden.
      this.disableHide(this.redirectSubmitTarget);
      this.hide(this.redirectTarget);
      this.toggleCommentsHint(false);
      this.disableHide(this.submitTarget);
      return;
    }

    if (this.issuesYesTarget.checked) {
      // Issues reported → route to the full verification form.
      this.hide(this.detailTarget);
      this.hide(this.attestTarget);
      this.disableHide(this.submitTarget);
      this.show(this.redirectTarget);
      this.show(this.redirectSubmitTarget);
      this.redirectSubmitTarget.disabled = false;
    } else {
      // No issues → clean-exit body.
      this.hide(this.redirectTarget);
      this.disableHide(this.redirectSubmitTarget);
      this.show(this.detailTarget);
      this.show(this.attestTarget);
      this.show(this.submitTarget);
      // "Would not rent again" needs a reason; the comment field becomes required
      // and gates the submit alongside the attestation.
      const commentRequired = this.rentAgainNoTarget.checked;
      this.toggleCommentsHint(commentRequired);
      const commentOk = !commentRequired || this.commentsTarget.value.trim() !== "";
      this.submitTarget.disabled = !(this.certTarget.checked && commentOk);
    }
  }

  bothAnswered() {
    return this.q1Answered() && this.q2Answered();
  }

  q2Answered() {
    return this.issuesNoTarget.checked || this.issuesYesTarget.checked;
  }

  // When the comment is required, show the "why" hint and drop the "optional" label
  // so the field never reads as optional while it's gating the submit. Mirror the
  // requirement to assistive tech via aria-required + aria-describedby so it's not
  // conveyed by visuals + a disabled button alone.
  toggleCommentsHint(required) {
    this.commentsTarget.setAttribute("aria-required", required ? "true" : "false");
    if (required) {
      this.commentsTarget.setAttribute("aria-describedby", this.commentsRequiredHintTarget.id);
      this.show(this.commentsRequiredHintTarget);
      this.hide(this.commentsOptionalTarget);
    } else {
      this.commentsTarget.removeAttribute("aria-describedby");
      this.hide(this.commentsRequiredHintTarget);
      this.show(this.commentsOptionalTarget);
    }
  }

  q1Answered() {
    return this.rentAgainYesTarget.checked || this.rentAgainNoTarget.checked;
  }

  show(el) {
    el.classList.remove("d-none");
  }

  hide(el) {
    el.classList.add("d-none");
  }

  // Hide a submit button and disable it, so a hidden button can't be triggered
  // (e.g. via the Enter key) while it's not the active choice.
  disableHide(el) {
    el.classList.add("d-none");
    el.disabled = true;
  }
}
